import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Snackbar } from "@mui/material";
import { Alert } from "@material-ui/lab";
import DemandeAchatService from "../../services/DemandeAchatService";
import PurchaseService from "../../services/PurchaseOrder";

const statutLabel = (s) => {
  switch (s) {
    case 0: return { text: "En attente", color: "#f0ad4e" };
    case 1: return { text: "En cours", color: "#5bc0de" };
    case 2: return { text: "Acceptée", color: "#5cb85c" };
    case 3: return { text: "Refusée", color: "#d9534f" };
    default: return { text: "-", color: "#aaa" };
  }
};

const DemandeAchatDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [userRole, setUserRole] = useState([]);
  const [demande, setDemande] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newRef, setNewRef] = useState("");
  const [refsDevis, setRefsDevis] = useState([]);
  const [checkingRef, setCheckingRef] = useState(false);
  const [checkingBDC, setCheckingBDC] = useState(false);

  const [devisAccepte, setDevisAccepte] = useState("");
  const [bonCommandeAchat, setBonCommandeAchat] = useState("");

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorAlertText, setErrorAlertText] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [successAlertText, setSuccessAlertText] = useState("");

  useEffect(() => {
    const status = localStorage.getItem("status");
    if (status) setUserRole(JSON.parse(status));
    loadDemande();
  }, []);

  const loadDemande = () => {
    setLoading(true);
    DemandeAchatService.getDemandeById(id)
      .then((response) => {
        setLoading(false);
        if (Object.keys(response.data)[0] === "success") {
          const d = response.data.success;
          setDemande(d);
          setRefsDevis(d.listeReferencesDevis || []);
          setDevisAccepte(d.devisAccepte || "");
          setBonCommandeAchat(d.bonCommandeAchat || "");
        }
      })
      .catch(() => {
        setLoading(false);
        setErrorAlertText("Erreur de chargement de la demande");
        setOpenErrorAlert(true);
      });
  };

  const isRA = userRole.includes("PURCHASING_MANAGER") || userRole.includes("ADMIN");

  const handleUpdateStatut = (statut) => {
    DemandeAchatService.updateStatut(id, statut)
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setSuccessAlertText("Statut mis à jour avec succès");
          setOpenSuccessAlert(true);
          loadDemande();
        } else {
          setErrorAlertText(response.data.error || "Erreur lors de la mise à jour");
          setOpenErrorAlert(true);
        }
      })
      .catch(() => {
        setErrorAlertText("Erreur de connexion");
        setOpenErrorAlert(true);
      });
  };

  const handleAddRef = () => {
    const ref = newRef.trim();
    if (!ref) return;

    if (refsDevis.includes(ref)) {
      setErrorAlertText("Cette référence de devis est déjà ajoutée");
      setOpenErrorAlert(true);
      return;
    }

    setCheckingRef(true);
    PurchaseService.verifieDevisExistanceByReference(ref)
      .then((response) => {
        setCheckingRef(false);
        if (response.data && Object.keys(response.data)[0] === "Exist") {
          setRefsDevis([...refsDevis, ref]);
          setNewRef("");
        } else {
          setErrorAlertText(`Devis "${ref}" introuvable dans le système`);
          setOpenErrorAlert(true);
        }
      })
      .catch(() => {
        setCheckingRef(false);
        setErrorAlertText("Erreur lors de la vérification du devis");
        setOpenErrorAlert(true);
      });
  };

  const handleRemoveRef = (index) => {
    setRefsDevis(refsDevis.filter((_, i) => i !== index));
  };

  const handleSaveRefs = () => {
    DemandeAchatService.addReferencesDevis(id, refsDevis)
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setSuccessAlertText("Références de devis enregistrées");
          setOpenSuccessAlert(true);
          loadDemande();
        } else {
          setErrorAlertText(response.data.error || "Erreur");
          setOpenErrorAlert(true);
        }
      })
      .catch(() => {
        setErrorAlertText("Erreur de connexion");
        setOpenErrorAlert(true);
      });
  };

  const handleAccepterDevis = () => {
    if (!devisAccepte.trim()) {
      setErrorAlertText("Erreur : le devis accepté est obligatoire");
      setOpenErrorAlert(true);
      return;
    }
    if (!bonCommandeAchat.trim()) {
      setErrorAlertText("Erreur : la référence du bon de commande est obligatoire");
      setOpenErrorAlert(true);
      return;
    }

    setCheckingBDC(true);
    PurchaseService.getBDCByRef(bonCommandeAchat.trim())
      .then((response) => {
        const hasData =
          response.data &&
          Object.keys(response.data).length > 0 &&
          !response.data.error &&
          !response.data.empty;

        if (!hasData) {
          setCheckingBDC(false);
          setErrorAlertText(`Bon de commande "${bonCommandeAchat}" introuvable dans le système`);
          setOpenErrorAlert(true);
          return;
        }

        DemandeAchatService.accepterDevis(id, devisAccepte, bonCommandeAchat)
          .then((res) => {
            setCheckingBDC(false);
            if (Object.keys(res.data)[0] === "success") {
              setSuccessAlertText("Demande acceptée avec succès");
              setOpenSuccessAlert(true);
              loadDemande();
            } else {
              setErrorAlertText(res.data.error || "Erreur");
              setOpenErrorAlert(true);
            }
          })
          .catch(() => {
            setCheckingBDC(false);
            setErrorAlertText("Erreur de connexion");
            setOpenErrorAlert(true);
          });
      })
      .catch(() => {
        setCheckingBDC(false);
        setErrorAlertText("Erreur lors de la vérification du bon de commande");
        setOpenErrorAlert(true);
      });
  };

  if (loading) {
    return (
      <div className="content-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <div className="spinner-border" style={{ color: "#343a40" }} />
      </div>
    );
  }

  if (!demande) {
    return (
      <div className="content-wrapper">
        <div className="alert alert-danger m-4">Demande introuvable.</div>
      </div>
    );
  }

  const s = statutLabel(demande.statut);

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">Achat</li>
                <li className="breadcrumb-item">
                  <a href="/demandesAchat">Demandes d'Achat</a>
                </li>
                <li className="breadcrumb-item active">{demande.reference}</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">

          {/* Informations générales */}
          <div className="card card-dark">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title">
                Demande d'Achat — {demande.reference}
              </h5>
              <span
                style={{
                  backgroundColor: s.color,
                  color: "#fff",
                  padding: "4px 14px",
                  borderRadius: 10,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {s.text}
              </span>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Référence</label>
                    <p className="form-control-plaintext font-weight-bold">{demande.reference}</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Date de création</label>
                    <p className="form-control-plaintext">
                      {demande.dateCreation
                        ? new Date(demande.dateCreation).toLocaleDateString("fr-FR")
                        : "-"}
                    </p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Demandeur</label>
                    <p className="form-control-plaintext">{demande.demandeur}</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Service</label>
                    <p className="form-control-plaintext">{demande.service}</p>
                  </div>
                </div>
                <div className="col-md-8">
                  <div className="form-group">
                    <label>Note / Justification</label>
                    <p className="form-control-plaintext">{demande.note || "-"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Produits demandés */}
          <div className="card card-dark">
            <div className="card-header">
              <h5 className="card-title">Produits demandés</h5>
            </div>
            <div className="card-body p-0">
              <table className="table table-bordered mb-0">
                <thead style={{ backgroundColor: "#fff", color: "#343a40" }}>
                  <tr>
                    <th>#</th>
                    <th>Désignation</th>
                    <th>Quantité</th>
                  </tr>
                </thead>
                <tbody>
                  {demande.listeProduits?.length > 0 ? (
                    demande.listeProduits.map((p, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{p.designation}</td>
                        <td>{p.quantite}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">Aucun produit</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* RA: Actions EN_ATTENTE */}
          {isRA && demande.statut === 0 && (
            <div className="card card-dark">
              <div className="card-header">
                <h5 className="card-title">Actions — Responsable Achat</h5>
              </div>
              <div className="card-body d-flex" style={{ gap: 10 }}>
                <button
                  className="btn btn-info"
                  onClick={() => handleUpdateStatut(1)}
                >
                  <i className="fas fa-play mr-1" /> Mettre EN COURS
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleUpdateStatut(3)}
                >
                  <i className="fas fa-times mr-1" /> Refuser
                </button>
              </div>
            </div>
          )}

          {/* RA: Gestion devis EN_COURS */}
          {isRA && demande.statut === 1 && (
            <>
              <div className="card card-dark">
                <div className="card-header">
                  <h5 className="card-title">Références de devis fournisseurs</h5>
                </div>
                <div className="card-body">
                  <div className="input-group mb-3" style={{ maxWidth: 500 }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ex: DV-2026-00015"
                      value={newRef}
                      onChange={(e) => setNewRef(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && !checkingRef && handleAddRef()}
                      disabled={checkingRef}
                    />
                    <div className="input-group-append">
                      <button
                        className="btn btn-dark"
                        onClick={handleAddRef}
                        disabled={checkingRef}
                      >
                        {checkingRef ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <i className="fas fa-plus" />
                        )}
                      </button>
                    </div>
                  </div>
                  {checkingRef && (
                    <small className="text-muted">
                      <i className="fas fa-circle-notch fa-spin mr-1" />
                      Vérification de la référence...
                    </small>
                  )}

                  {refsDevis.length > 0 && (
                    <table className="table table-bordered table-sm mb-3" style={{ maxWidth: 500 }}>
                      <thead style={{ backgroundColor: "#fff", color: "#343a40" }}>
                        <tr>
                          <th>Référence devis</th>
                          <th style={{ width: 60 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {refsDevis.map((ref, i) => (
                          <tr key={i}>
                            <td>
                              <span
                                style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => window.open(`/getDocumentByRef?ref=${ref}&type=devis`, "_blank")}
                              >
                                {ref}
                              </span>
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleRemoveRef(i)}
                              >
                                <i className="fas fa-times" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <button className="btn btn-dark" onClick={handleSaveRefs}>
                    <i className="fas fa-save mr-1" /> Enregistrer les devis
                  </button>
                </div>
              </div>

              {/* Accepter un devis */}
              <div className="card card-dark">
                <div className="card-header">
                  <h5 className="card-title">Accepter un devis</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-5">
                      <div className="form-group">
                        <label>
                          Devis accepté <span style={{ color: "red" }}>*</span>
                        </label>
                        <select
                          className="form-control"
                          value={devisAccepte}
                          onChange={(e) => setDevisAccepte(e.target.value)}
                        >
                          <option value="">-- Sélectionner le devis retenu --</option>
                          {refsDevis.map((ref, i) => (
                            <option key={i} value={ref}>{ref}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-5">
                      <div className="form-group">
                        <label>
                          Référence bon de commande <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Ex: BCA-2026-00008"
                          value={bonCommandeAchat}
                          onChange={(e) => setBonCommandeAchat(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <div className="form-group w-100">
                        <button
                          className="btn btn-success w-100"
                          onClick={handleAccepterDevis}
                          disabled={checkingBDC}
                        >
                          {checkingBDC ? (
                            <span className="spinner-border spinner-border-sm mr-1" />
                          ) : (
                            <i className="fas fa-check mr-1" />
                          )}
                          {checkingBDC ? "Vérification..." : "Accepter"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Résumé ACCEPTEE */}
          {demande.statut === 2 && (
            <div className="card card-dark">
              <div className="card-header">
                <h5 className="card-title">Résumé de la décision</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Devis retenu</label>
                      <p>
                        <span
                          className="badge badge-success"
                          style={{ fontSize: 13, cursor: "pointer" }}
                          title="Voir le détail"
                          onClick={() => window.open(`/getDocumentByRef?ref=${demande.devisAccepte}&type=devis`, "_blank")}
                        >
                          {demande.devisAccepte}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Bon de commande achat</label>
                      <p>
                        <span
                          className="badge badge-dark"
                          style={{ fontSize: 13, cursor: "pointer" }}
                          title="Voir le détail"
                          onClick={() => window.open(`/getDocumentByRef?ref=${demande.bonCommandeAchat}&type=bdc`, "_blank")}
                        >
                          {demande.bonCommandeAchat}
                        </span>
                      </p>
                    </div>
                  </div>
                  {demande.listeReferencesDevis?.length > 0 && (
                    <div className="col-md-12">
                      <div className="form-group">
                        <label>Tous les devis reçus</label>
                        <p>
                          {demande.listeReferencesDevis.map((r, i) => (
                            <span
                              key={i}
                              className="badge badge-secondary mr-1"
                              style={{ fontSize: 12, cursor: "pointer" }}
                              title="Voir le détail"
                              onClick={() => window.open(`/getDocumentByRef?ref=${r}&type=devis`, "_blank")}
                            >
                              {r}
                            </span>
                          ))}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* REFUSEE */}
          {demande.statut === 3 && (
            <div className="alert alert-danger">
              <i className="fas fa-ban mr-2" />
              Cette demande a été <strong>refusée</strong> par le Responsable Achat.
            </div>
          )}

          <div className="mt-3">
            <button className="btn btn-dark" onClick={() => navigate("/demandesAchat")}>
              <i className="fas fa-arrow-left mr-1" /> Retour à la liste
            </button>
          </div>
        </div>
      </section>

      <Snackbar
        open={openErrorAlert}
        autoHideDuration={4000}
        onClose={() => setOpenErrorAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenErrorAlert(false)} severity="error">
          {errorAlertText}
        </Alert>
      </Snackbar>

      <Snackbar
        open={openSuccessAlert}
        autoHideDuration={3000}
        onClose={() => setOpenSuccessAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenSuccessAlert(false)} severity="success">
          {successAlertText}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DemandeAchatDetail;
